import { create } from 'zustand';
import { endpoints, type MicrosoftAccount, type ServerConfig, type User } from '../services/api';
import { switchProfile } from './switchProfile';

export interface ProviderStatus {
  connected: boolean;
  email: string | null;
  /** Microsoft To Do permission granted (accounts connected earlier must reconnect). */
  tasks?: boolean;
  /** Work/school account (SharePoint, shared calendars, Planner available). */
  work?: boolean;
  /** Extra work access (SharePoint, shared files, shared calendars) granted. */
  workAccess?: boolean;
  /** Every connected account (the fields above summarise them). */
  accounts?: MicrosoftAccount[];
}

interface SessionStore {
  config: ServerConfig | null;
  user: User | null;
  loaded: boolean;
  microsoft: ProviderStatus | null;
  /** A Microsoft account couldn't be connected: it belongs to another KCL Canvas account. */
  linkProblem: { code: 'linked_elsewhere' | 'only_sign_in'; email: string } | null;
  setLinkProblem: (p: SessionStore['linkProblem']) => void;
  load: () => Promise<void>;
  setUser: (u: User | null) => void;
  refreshMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
}

const offlineConfig: ServerConfig = {
  env: 'offline',
  features: { accounts: false, billing: false, microsoft: false },
  licensePrice: { amount: 1000, currency: 'GBP' },
};

export const useSession = create<SessionStore>()((set, get) => ({
  config: null,
  user: null,
  loaded: false,
  microsoft: null,
  linkProblem: null,
  setLinkProblem: (linkProblem) => set({ linkProblem }),
  load: async () => {
    const [config, me] = await Promise.allSettled([endpoints.config(), endpoints.me()]);
    const user = me.status === 'fulfilled' ? me.value.user : null;
    // If the session couldn't be checked (offline), keep showing the last account's data.
    if (me.status === 'fulfilled') switchProfile(user?.id);
    set({ config: config.status === 'fulfilled' ? config.value : offlineConfig, user, loaded: true });
    await get().refreshMicrosoft();
  },
  setUser: (user) => {
    // Swap to this account's local data before anything (e.g. settings sync) sees the user.
    switchProfile(user?.id);
    set({ user });
    void get().refreshMicrosoft();
  },
  refreshMicrosoft: async () => {
    const { user, config } = get();
    if (!user || !config?.features.microsoft) {
      set({ microsoft: null });
      return;
    }
    try {
      set({ microsoft: await endpoints.microsoftStatus() });
    } catch {
      set({ microsoft: null });
    }
  },
  signOut: async () => {
    await endpoints.logout().catch(() => undefined);
    switchProfile(null);
    set({ user: null, microsoft: null });
  },
}));

export const isLicensed = (u: User | null): boolean => u?.license === 'lifetime';

/**
 * Starts the Microsoft OAuth flow (full-page redirect). `returnTo` is an app path, e.g. /pair.
 * `link` connects another Microsoft account (Microsoft shows its account chooser). `work`
 * (link only) asks a work/school account – `account`, else the first – for SharePoint,
 * shared files and shared calendars.
 */
export function startMicrosoft(
  mode: 'signin' | 'link',
  returnTo?: string,
  work = false,
  account?: string,
  /** Take the account over from another KCL Canvas account (pre-selecting `hint`). */
  move?: { hint: string },
): void {
  const q = new URLSearchParams({ mode });
  if (returnTo) q.set('returnTo', returnTo);
  if (work && mode === 'link') q.set('access', 'work');
  if (work && account) q.set('account', account);
  if (move && mode === 'link') {
    q.set('move', '1');
    if (move.hint) q.set('hint', move.hint);
  }
  window.location.assign(`/api/auth/oauth/microsoft/start?${q}`);
}
